import * as yaml from 'js-yaml';
import {
  ExportableWorkflowDefinition,
  ExportOptions,
  ExportResult,
  SCHEMA_VERSION,
} from './types';
import { CompleteWorkflowDefinition } from 'src/stores/workflowDefinitionCanvasStore/workflowDefinitionCanvasStore.interface';


export function generateExportFilename(
  workflowName: string,
  format: 'json' | 'yaml'
): string {
  const sanitizedName = workflowName
    .replace(/[^a-zA-Z0-9-_]/g, '_')
    .replace(/_+/g, '_')
    .substring(0, 50);

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const extension = format === 'yaml' ? 'yaml' : 'json';

  return `${sanitizedName}_${timestamp}.${extension}`;
}

export function convertToExportableWorkflow(
  workflow: CompleteWorkflowDefinition,
): ExportableWorkflowDefinition {
  return {
    schemaVersion: SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    workflowDefinition: workflow.workflowDefinition,
    taskDefinitions: workflow.taskDefinitions,
    dependencies: workflow.dependencies,
  };
}

export function serializeToJson(workflowDefinition: ExportableWorkflowDefinition): string {
  return JSON.stringify(workflowDefinition, null, 2);
}

export function serializeToYaml(workflowDefinition: ExportableWorkflowDefinition): string {
  return yaml.dump(workflowDefinition, {
    indent: 2,
    lineWidth: 120,
    noRefs: true,
    sortKeys: false,
  });
}

export function triggerDownload(
  content: string,
  filename: string,
  mimeType: string
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function exportWorkflow(
  workflowDefinition: CompleteWorkflowDefinition,
  options: ExportOptions,
): Promise<ExportResult> {
  try {
    if (!workflowDefinition.taskDefinitions || workflowDefinition.taskDefinitions.length === 0) {
      return {
        success: false,
        filename: '',
        error: 'Cannot export an empty workflow. Add at least one task before exporting.',
      };
    }

    const exportable = convertToExportableWorkflow(
      workflowDefinition,
    );

    let content: string;
    let mimeType: string;

    if (options.format === 'yaml') {
      content = serializeToYaml(exportable);
      mimeType = 'application/x-yaml';
    } else {
      content = serializeToJson(exportable);
      mimeType = 'application/json';
    }

    const filename = generateExportFilename(
      exportable.workflowDefinition.workflowDefinitionName || 'workflow',
      options.format
    );

    triggerDownload(content, filename, mimeType);

    return {
      success: true,
      filename,
    };
  } catch (error) {
    return {
      success: false,
      filename: '',
      error: error instanceof Error ? error.message : 'An unexpected error occurred during export.',
    };
  }
}
